/* La Thé Box — pages boutique : l'état actif de la barre de filtres.
   Passation : _design/boutique/03-collection-maison/

   Les pastilles sont des ancres vers les groupes de la grille. Ce script ne
   fait qu'une chose : marquer celle du groupe qu'on est en train de lire.
   Sans lui, les ancres marchent toujours — c'est un confort, pas une
   dépendance.

   Un écouteur de défilement étranglé par `requestAnimationFrame`, et non un
   IntersectionObserver : le repère cherché est « le dernier titre passé sous
   la barre », qui se lit d'un coup sur les positions, alors qu'un observateur
   ne signale que les franchissements et rend ses entrées dans le désordre. */
(() => {
  const barre = document.querySelector('[data-ltb-filtres]');
  if (!barre) return;

  const pastilles = new Map();
  barre.querySelectorAll('[data-filtre]').forEach((a) => pastilles.set(a.dataset.filtre, a));
  if (!pastilles.size) return;

  const groupes = [...pastilles.keys()].map((id) => document.getElementById(id)).filter(Boolean);
  if (!groupes.length) return;

  let courant = null;
  let attente = null;

  const relever = () => {
    attente = null;
    // La barre est collante : un titre est « lu » quand il passe sous elle,
    // pas quand il touche le haut de la fenêtre.
    const seuil = barre.getBoundingClientRect().height + 24;
    let actif = groupes[0];
    groupes.forEach((g) => {
      if (g.getBoundingClientRect().top <= seuil) actif = g;
    });
    if (actif.id === courant) return;
    courant = actif.id;
    pastilles.forEach((a, cle) => a.classList.toggle('is-active', cle === courant));
  };

  const planifier = () => {
    if (attente) return;
    attente = requestAnimationFrame(relever);
  };

  window.addEventListener('scroll', planifier, { passive: true });
  window.addEventListener('resize', planifier, { passive: true });
  relever();
})();

/* Pagination par lot — « voir plus » sans recharger.

   Le bouton reste un vrai lien vers la page suivante : sans JavaScript, il
   emmène à la page 2 et la grille s'affiche normalement. Ici on intercepte le
   clic pour aller chercher cette même page, ajouter ses cartes à la grille en
   place, et remplacer le bloc de pagination par celui de la page chargée —
   qui porte déjà le bon compteur, la bonne jauge et le lien suivant, puisque
   Liquid les calcule à partir du décalage de page.

   `brancherPagination` est exposé plutôt qu'enfermé dans sa fonction : le
   filtrage sans rechargement remplace la zone de grille, donc le bloc de
   pagination, et il faut rebrancher le nouveau. Le drapeau `data-branche`
   évite de brancher deux fois le même. */
function brancherPagination() {
  const bloc = document.querySelector('[data-ltb-pagination]:not([data-pagi-local])');
  if (!bloc || !bloc.dataset.cible || bloc.dataset.branche) return;

  const grille = document.querySelector(bloc.dataset.cible);
  if (!grille) return;

  bloc.dataset.branche = '1';
  let enCours = false;

  bloc.addEventListener('click', async (e) => {
    const bouton = e.target.closest('[data-pagi-suivant]');
    if (!bouton || enCours) return;
    e.preventDefault();
    enCours = true;
    bouton.setAttribute('aria-busy', 'true');

    try {
      const reponse = await fetch(bouton.href, { headers: { 'X-Requested-With': 'fetch' } });
      if (!reponse.ok) throw new Error(reponse.status);
      const doc = new DOMParser().parseFromString(await reponse.text(), 'text/html');

      const suite = doc.querySelector(bloc.dataset.cible);
      const pagination = doc.querySelector('[data-ltb-pagination]');
      if (!suite || !pagination) throw new Error('page suivante illisible');

      const premiere = suite.firstElementChild;
      grille.append(...suite.children);
      bloc.innerHTML = pagination.innerHTML;

      // L'URL ne bouge pas : elle désigne la page 2 seule, alors que l'écran
      // montre les pages 1 et 2 cumulées. La réécrire ferait mentir un
      // rechargement ou un partage.

      // Le clavier reprend là où le regard s'est arrêté — `premiere` a été
      // déplacée dans la grille par `append`, c'est bien le premier produit
      // du nouveau lot.
      const lien = premiere && premiere.querySelector('a');
      if (lien) { lien.setAttribute('tabindex', '-1'); lien.focus({ preventScroll: true }); }
    } catch (err) {
      // On rend la main au navigateur : le lien fait ce qu'il a toujours su
      // faire, changer de page.
      window.location.href = bouton.href;
    } finally {
      enCours = false;
      bouton.removeAttribute('aria-busy');
    }
  });
}
brancherPagination();

/* Filtrage sans rechargement — pages envie, famille et bonnes affaires.

   Les pastilles restent **les filtres natifs de Shopify** : de vrais liens
   vers la même collection avec un paramètre `filter.*` de plus. C'est la
   boutique qui filtre, compte, trie et pagine, sur les 166 produits de la
   collection et pas sur les 24 de l'écran. Rien de tout cela n'est réécrit
   ici — on évite seulement de repeindre la page entière pour le lire.

   Le mécanisme est celui de Shopify : la même URL, plus `section_id`, rend la
   section seule. On en récupère trois morceaux, et trois seulement :

   - `[data-ltb-entete]`, parce que **les chiffres du héro suivent la
     sélection** — 166 produits deviennent 84, 43 maisons deviennent 31. Les
     laisser en place les ferait mentir, ce qui serait pire que de recharger ;
   - `[data-ltb-filtres]`, pour les compteurs de chaque pastille, l'état actif
     et la pastille « Tout voir » qui apparaît dès qu'un filtre est posé ;
   - `#la-grille`, les cartes et la pagination.

   Les trois éléments d'accueil ne sont jamais remplacés, seulement remplis :
   l'écouteur posé une fois sur la barre survit à tous les échanges.

   **Une page n'entre dans ce mode que si elle porte `data-ltb-entete`.** La
   page maison ne l'a pas : ses pastilles sont des ancres, pas des filtres, et
   ce serait un contresens de les intercepter. C'est l'attribut qui décide,
   pas ce script.

   À la moindre anomalie — réponse en erreur, section illisible, morceau
   manquant — on rend la main au navigateur, qui fait ce qu'il a toujours su
   faire : suivre le lien. La page se recharge, et rien n'est perdu. */
(() => {
  const entete = document.querySelector('[data-ltb-entete]');
  const barre = document.querySelector('[data-ltb-filtres]');
  const zone = document.getElementById('la-grille');
  if (!entete || !barre || !zone) return;

  const enveloppe = barre.closest('.shopify-section');
  const prefixe = 'shopify-section-';
  if (!enveloppe || !enveloppe.id.startsWith(prefixe)) return;
  const sectionId = enveloppe.id.slice(prefixe.length);
  if (!sectionId) return;

  // Un lien n'est repris que s'il désigne la même page avec d'autres
  // paramètres. Les segments des bonnes affaires visent une autre collection :
  // ceux-là gardent leur navigation pleine, et c'est voulu.
  const memePage = (url) => url.origin === location.origin && url.pathname === location.pathname;

  let enCours = null;

  const brancherTri = () => {
    const menu = barre.querySelector('.ltb-filtres__tri-menu');
    if (!menu || menu.dataset.branche) return;
    // L'attribut `onchange` du gabarit envoie le formulaire et recharge. Il
    // reste dans le HTML pour qui n'exécute pas ce script ; ici on le retire
    // au profit d'un aller-retour discret.
    menu.removeAttribute('onchange');
    menu.dataset.branche = '1';
    menu.addEventListener('change', () => {
      const form = menu.form;
      if (!form) return;
      const url = new URL(location.pathname, location.origin);
      new FormData(form).forEach((v, k) => { if (v !== '') url.searchParams.append(k, v); });
      aller(url.toString());
    });
  };

  const annoncer = () => {
    const total = barre.querySelector('.ltb-filtres__total');
    if (total) { total.setAttribute('role', 'status'); total.setAttribute('aria-live', 'polite'); }
  };

  const rebrancher = () => { brancherTri(); annoncer(); brancherPagination(); };

  async function aller(href, pousser = true, libelle = null) {
    if (enCours) enCours.abort();
    const signal = (enCours = new AbortController()).signal;

    zone.setAttribute('aria-busy', 'true');
    zone.setAttribute('data-ltb-occupe', '');

    try {
      const u = new URL(href, location.origin);
      u.searchParams.set('section_id', sectionId);

      const reponse = await fetch(u, { headers: { 'X-Requested-With': 'fetch' }, signal });
      if (!reponse.ok) throw new Error(reponse.status);
      const doc = new DOMParser().parseFromString(await reponse.text(), 'text/html');

      const nEntete = doc.querySelector('[data-ltb-entete]');
      const nBarre = doc.querySelector('[data-ltb-filtres]');
      const nZone = doc.getElementById('la-grille');
      if (!nEntete || !nBarre || !nZone) throw new Error('section illisible');

      entete.innerHTML = nEntete.innerHTML;
      barre.innerHTML = nBarre.innerHTML;
      zone.innerHTML = nZone.innerHTML;

      if (pousser) history.pushState({ ltb: 1 }, '', href);
      rebrancher();

      // On ne déplace pas la page : la barre est collante, elle est donc sous
      // les yeux au moment du clic, et la grille se renouvelle dessous. Le
      // clavier, lui, doit retrouver la pastille qu'il vient d'activer.
      if (libelle) {
        const reprise = [...barre.querySelectorAll('.ltb-filtres__pastille')]
          .find((a) => a.textContent.trim().startsWith(libelle));
        if (reprise) reprise.focus({ preventScroll: true });
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      window.location.href = href;
    } finally {
      // Un clic plus récent a pu prendre la main : c'est lui qui décidera
      // quand la grille cesse d'être occupée, pas cet appel-ci.
      if (enCours && enCours.signal === signal) {
        enCours = null;
        zone.removeAttribute('aria-busy');
        zone.removeAttribute('data-ltb-occupe');
      }
    }
  }

  barre.addEventListener('click', (e) => {
    const lien = e.target.closest('a[href]');
    if (!lien || !barre.contains(lien)) return;
    // Un clic du milieu, ou avec une touche de modification, ouvre un onglet :
    // c'est au navigateur de le faire, pas à nous.
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    let url;
    try { url = new URL(lien.href, location.origin); } catch (_) { return; }
    if (!memePage(url)) return;

    e.preventDefault();
    const compte = lien.querySelector('.ltb-filtres__compte');
    const libelle = (compte ? lien.textContent.replace(compte.textContent, '') : lien.textContent).trim();
    aller(lien.href, true, libelle);
  });

  // Retour et suivant du navigateur : l'URL porte toute la sélection, il n'y
  // a donc rien à mémoriser. On ne repousse pas d'entrée d'historique.
  window.addEventListener('popstate', () => {
    if (memePage(new URL(location.href))) aller(location.href, false);
  });

  rebrancher();
})();

/* Pagination locale — la page nouveautés.

   Son périmètre est une fenêtre de dates, que Shopify ne sait pas paginer :
   toutes les cartes sont donc déjà dans la page, les suivantes portant
   l'attribut `hidden`. Le bouton en découvre un lot de plus.

   Sans JavaScript, tout s'affiche d'un coup — une page complète, pas une
   page cassée. C'est le sens de la dégradation choisie ici. */
(() => {
  const bloc = document.querySelector('[data-ltb-pagination][data-pagi-local]');
  if (!bloc || !bloc.dataset.cible) return;

  const grille = document.querySelector(bloc.dataset.cible);
  if (!grille) return;

  const lot = parseInt(bloc.dataset.pagiLot, 10) || 12;
  const total = parseInt(bloc.dataset.pagiTotal, 10) || 0;
  const modeles = {
    progression: bloc.dataset.modeleProgression || '[v] sur [t]',
    total: bloc.dataset.modeleTotal || '[t] référence[s]',
    bouton: bloc.dataset.modeleBouton || 'Voir le[s] [n] suivant[s]',
  };

  // `[s]` s'accorde avec le nombre que le libellé annonce — « 1 produit sur
  // 12 », « 12 produits sur 12 ». Le même calcul est fait en Liquid au
  // chargement (`ltb-pagination`) ; sans celui-ci, l'accord serait juste à
  // l'arrivée sur la page puis faux dès le premier « voir plus ».
  const accord = (modele, n) => modele.replace('[s]', n > 1 ? 's' : '');

  const compteur = bloc.querySelector('[data-pagi-compteur]');
  const barre = bloc.querySelector('[data-pagi-barre]');
  const jauge = bloc.querySelector('.ltb-pagi__jauge');
  const bouton = bloc.querySelector('[data-pagi-suivant]');
  if (!bouton) return;

  const caches = () => [...grille.children].filter((c) => c.hidden);

  bouton.addEventListener('click', () => {
    const suite = caches().slice(0, lot);
    // `hidden` et non `style.display` : la carte reprend l'affichage que sa
    // feuille lui donne, quel qu'il soit.
    suite.forEach((c) => { c.hidden = false; });

    const montres = total - caches().length;
    const reste = total - montres;

    if (reste <= 0) {
      // Tout est affiché : le compteur se réduit au total et la jauge s'en va.
      // Une barre pleine se lit comme un chargement bloqué.
      compteur.textContent = accord(modeles.total, total).replace('[t]', total);
      if (barre) barre.remove();
      bouton.remove();
    } else {
      compteur.textContent = accord(modeles.progression, montres).replace('[v]', montres).replace('[t]', total);
      if (jauge) jauge.style.width = Math.round((montres / total) * 100) + '%';
      const prochain = Math.min(lot, reste);
      bouton.textContent = accord(modeles.bouton, prochain).replace('[n]', prochain);
    }

    // Le clavier reprend au premier produit découvert.
    const lien = suite[0] && suite[0].querySelector('a');
    if (lien) { lien.setAttribute('tabindex', '-1'); lien.focus({ preventScroll: true }); }
  });
})();
